import React, { useEffect, useState } from "react";
import MobileLayout from "../components/layout/MobileLayout";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, FileVideo, Megaphone, Lightbulb, Share2, Settings as SettingsIcon, Headphones } from "lucide-react";
import { useI18n, LANGS } from "../lib/i18n";

const LANG_OPTIONS = LANGS;
const useI18nFromCtx = () => useI18n();

/** Shared premium dark glass panel */
const panelClass =
  "rounded-2xl p-4 shadow-xl backdrop-blur-sm";
const panelStyle = {
  background: "linear-gradient(180deg, rgba(10,24,51,0.65) 0%, rgba(6,14,40,0.9) 100%)",
  border: "1px solid rgba(34,211,238,0.25)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(34,211,238,0.08) inset",
};

export function SupportPage() {
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get("/settings").then(({data}) => setSettings(data)); }, []);
  const wa = (settings.whatsapp_number || "+919999999999").replace(/\D/g, "");
  return (
    <MobileLayout>
      <h1 className="font-display font-bold text-2xl tracking-tight text-gold-static mb-3">Support</h1>
      <div className={`${panelClass} mb-3 text-center`} style={panelStyle}>
        <Headphones className="w-12 h-12 mx-auto text-cyan-300 mb-3" />
        <div className="font-display font-semibold text-white">24×7 Customer Care</div>
        <div className="text-sm text-cyan-200/70 mt-1">Our team is here to assist you anytime.</div>
      </div>
      <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="support-whatsapp"
        className="flex items-center gap-3 rounded-2xl p-4 mb-2 border border-emerald-400/40 shadow-lg active:scale-[0.99] transition"
        style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(5,150,105,0.08))" }}>
        <div className="w-10 h-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center"><MessageCircle className="w-5 h-5" /></div>
        <div>
          <div className="font-semibold text-emerald-100">WhatsApp Support</div>
          <div className="text-xs text-emerald-200/80">{settings.whatsapp_number || "+91 9999999999"}</div>
        </div>
      </a>
      <a href={`tel:${wa}`} className={`flex items-center gap-3 ${panelClass}`} style={panelStyle} data-testid="support-call">
        <div className="w-10 h-10 rounded-lg bg-cyan-500 text-white flex items-center justify-center"><Phone className="w-5 h-5" /></div>
        <div>
          <div className="font-semibold text-white">Call Support</div>
          <div className="text-xs text-cyan-200/70">Tap to call</div>
        </div>
      </a>
    </MobileLayout>
  );
}

export function NoticePage() {
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get("/settings").then(({data}) => setSettings(data)); }, []);
  const rules = [
    "Players must be 18 years or older to participate.",
    "All bids are final once submitted to the bet slip and confirmed.",
    "Results are declared at the official close time as per market schedule.",
    "Winnings are credited to wallet automatically after result declaration.",
    "Minimum deposit is 100 points; minimum withdrawal is 500 points.",
    "Deposits and withdrawals are processed by admin within 30 minutes.",
    "Use of multiple accounts is strictly prohibited and will result in a ban.",
    "Play responsibly — set limits and never bid more than you can afford to lose.",
  ];
  return (
    <MobileLayout>
      <h1 className="font-display font-bold text-2xl tracking-tight text-gold-static mb-3">Notice Board & Rules</h1>
      <div className="rounded-2xl p-4 mb-3 flex gap-3 border border-amber-400/40"
        style={{ background: "linear-gradient(135deg, rgba(251,191,36,0.14), rgba(217,119,6,0.08))" }}>
        <Megaphone className="w-5 h-5 text-amber-300 shrink-0" />
        <div className="text-sm text-amber-100">{settings.notice_text}</div>
      </div>
      <div className={panelClass} style={panelStyle}>
        <div className="font-display font-semibold text-white mb-2">Game Rules</div>
        <ol className="space-y-2 text-sm text-cyan-100/90 list-decimal list-inside">
          {rules.map((r, i) => <li key={i}>{r}</li>)}
        </ol>
      </div>
    </MobileLayout>
  );
}

export function RatesPage() {
  const [games, setGames] = useState([]);
  useEffect(() => { api.get("/games").then(({data}) => setGames(data)); }, []);
  return (
    <MobileLayout>
      <h1 className="font-display font-bold text-2xl tracking-tight text-gold-static mb-3">Game Rates</h1>
      <div className="rounded-2xl overflow-hidden shadow-xl backdrop-blur-sm" style={panelStyle} data-testid="rates-table">
        {games.map((g, i) => (
          <div key={g.key} className={`flex items-center justify-between px-4 py-3 ${i !== games.length - 1 ? "border-b border-cyan-400/10" : ""}`}>
            <div className="text-sm text-white">{g.name}</div>
            <div className="text-sm font-display font-bold text-amber-300 tabular-nums">1× → {g.rate}×</div>
          </div>
        ))}
      </div>
    </MobileLayout>
  );
}

export function ChartsPage() {
  const [markets, setMarkets] = useState([]);
  useEffect(() => { api.get("/markets").then(({data}) => setMarkets(data)); }, []);
  return (
    <MobileLayout>
      <h1 className="font-display font-bold text-2xl tracking-tight text-gold-static mb-3">Charts</h1>
      <div className="space-y-2">
        {markets.map(m => (
          <div key={m.id} className={panelClass} style={panelStyle} data-testid={`chart-${m.id}`}>
            <div className="font-display font-semibold text-white">{m.name}</div>
            <div className="result-digits text-xl text-amber-300 mt-2">{m.live_result}</div>
            <div className="text-[11px] text-cyan-200/60 mt-1">Today's declared result</div>
          </div>
        ))}
      </div>
    </MobileLayout>
  );
}

export function TutorialsPage() {
  return (
    <MobileLayout>
      <h1 className="font-display font-bold text-2xl tracking-tight text-gold-static mb-3">Video Tutorials</h1>
      <div className={`${panelClass} text-center`} style={panelStyle}>
        <FileVideo className="w-12 h-12 mx-auto text-cyan-300/60 mb-3" />
        <div className="font-medium text-white">Tutorials coming soon</div>
        <div className="text-xs text-cyan-200/70 mt-1">Step-by-step guides for every game type.</div>
      </div>
    </MobileLayout>
  );
}

export function IdeaPage() {
  const [text, setText] = useState("");
  const send = () => {
    if (text.length < 10) return alert("Please describe your idea (min 10 chars)");
    alert("Thanks for your suggestion!");
    setText("");
  };
  return (
    <MobileLayout>
      <h1 className="font-display font-bold text-2xl tracking-tight text-gold-static mb-3">Submit an Idea</h1>
      <div className={panelClass} style={panelStyle}>
        <Lightbulb className="w-6 h-6 text-amber-300 mb-2" />
        <textarea data-testid="idea-text" value={text} onChange={(e) => setText(e.target.value)} rows={6}
          className="w-full rounded-lg p-3 text-sm bg-black/40 border border-cyan-400/30 text-white placeholder:text-cyan-200/40 focus:ring-2 focus:ring-cyan-400 focus:outline-none"
          placeholder="Share your ideas to improve Reddy Club…" />
        <button data-testid="idea-submit" onClick={send}
          className="w-full h-11 rounded-lg mt-3 font-black text-white tracking-wide"
          style={{ background: "linear-gradient(135deg,#22d3ee 0%,#2563eb 100%)" }}>
          Submit
        </button>
      </div>
    </MobileLayout>
  );
}

export function SettingsPage() {
  return <SettingsPageImpl />;
}

function SettingsPageImpl() {
  const { lang, setLang } = useI18nFromCtx();
  return (
    <MobileLayout>
      <h1 className="font-display font-bold text-2xl tracking-tight text-gold-static mb-3">Settings</h1>
      <div className={`${panelClass} mb-3`} style={panelStyle} data-testid="language-section">
        <div className="font-display font-semibold text-white mb-1">Language / भाषा</div>
        <p className="text-xs text-cyan-200/70 mb-3">Select your preferred language for the app interface.</p>
        <div className="grid grid-cols-2 gap-2">
          {LANG_OPTIONS.map(opt => (
            <button key={opt.code} onClick={() => setLang(opt.code)} data-testid={`lang-${opt.code}`}
              className={`text-sm font-medium py-3 rounded-lg border transition ${lang === opt.code
                ? "bg-gradient-to-br from-amber-400 to-yellow-500 text-blue-950 border-yellow-300 shadow-[0_0_14px_rgba(251,191,36,0.4)]"
                : "bg-black/30 text-cyan-100 border-cyan-400/25 hover:border-cyan-400"}`}>
              <div className="text-base">{opt.native}</div>
              <div className="text-[10px] uppercase tracking-widest opacity-70 mt-0.5">{opt.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div className={`${panelClass} mb-3`} style={panelStyle}>
        <Link to="/mpin" className="block">
          <div className="font-display font-semibold text-white">Change Password</div>
          <p className="text-xs text-cyan-200/70 mt-0.5">Update your account password</p>
        </Link>
      </div>

      <div className={panelClass} style={panelStyle}>
        <SettingsIcon className="w-6 h-6 text-cyan-300 mb-2" />
        <div className="text-sm text-cyan-100/90">More preferences coming soon.</div>
      </div>
    </MobileLayout>
  );
}

export function SharePage() {
  const link = window.location.origin;
  const copy = async () => {
    await navigator.clipboard.writeText(`Play Matka on Reddy Club — get ₹50 welcome bonus! ${link}`);
    alert("Invite copied!");
  };
  return (
    <MobileLayout>
      <h1 className="font-display font-bold text-2xl tracking-tight text-gold-static mb-3">Share App</h1>
      <div className="rounded-2xl p-5 text-white shadow-2xl"
        style={{ background: "linear-gradient(135deg,#f59e0b 0%,#dc2626 100%)" }}>
        <Share2 className="w-10 h-10 mb-3" />
        <div className="font-display font-bold text-xl tracking-tight">Refer & Earn</div>
        <div className="text-sm opacity-90 mt-1">Share Reddy Club with friends and earn rewards on every signup.</div>
        <button data-testid="share-copy" onClick={copy} className="mt-4 bg-white/95 text-amber-700 font-semibold px-4 py-2.5 rounded-lg w-full">Copy Invite Link</button>
      </div>
    </MobileLayout>
  );
}
